import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {useEffect, useRef} from 'react';
import {
  CHECKLIST_NOTES_RESET_TYPES,
  createDefaultShift,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../../types';
import {canDeleteShift} from './shiftReferenceUtils';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import {SVG} from '../../../../../../../constants/assetLists';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {getManualShifts, getTemplateShifts} from './defaultShift';
import {Radio} from '@mui/material';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import {TIMES} from '../../../../../../operations/components/Modals/AddAnnouncement/components/Time';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function Shifts({selectedFieldPath}: Props) {
  const {getDefaultSelectedNode} = useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);

  const didInitRef = useRef(false);
  const {showSnackbar} = useSnackbar();

  const {control, setValue, getValues, watch} =
    useFormContext<NetworkDetailsFormValues>();
  const network = watch('network') ?? [];
  const {fields, append, remove, replace} = useFieldArray({
    control,
    name: `${selectedFieldPath}.shifts`,
    keyName: 'id',
  });

  useEffect(() => {
    if (didInitRef.current) return;

    if (fields.length === 0) {
      replace(
        defaultSelectedNode.shift === 'manual'
          ? getManualShifts()
          : getTemplateShifts(),
      );
    }

    didInitRef.current = true;
  }, [
    defaultSelectedNode.shift,
    fields.length,
    replace,
    selectedFieldPath,
    setValue,
  ]);

  const resetType = useWatch({
    control,
    name: `${selectedFieldPath}.checklistNotesReset.type`,
  });

  useEffect(() => {
    if (resetType !== CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME) {
      setValue(`${selectedFieldPath}.checklistNotesReset.time`, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [resetType, selectedFieldPath, setValue]);

  const handleAddShift = () => {
    append(createDefaultShift());
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between p-2.5 border border-gray70 rounded-lg shadow-sm">
          <div className="flex items-center gap-10">
            <p className="m-0 text-xl font-bold text-darkBlue">
              שעות פעילות הארגון
            </p>

            <div className="flex gap-2">
              <Controller
                control={control}
                name={`${selectedFieldPath}.operatingHours.startTime`}
                render={({field, fieldState: {error}}) => (
                  <div className="w-[90px]">
                    <AppMUIAutocomplete
                      freeSolo
                      disableClearable
                      options={TIMES.map(i => i.title)}
                      label="מ-"
                      size="small"
                      required
                      value={field.value}
                      onChange={(_, v) =>
                        setValue(
                          `${selectedFieldPath}.operatingHours.startTime`,
                          v,
                          {shouldValidate: true, shouldDirty: true},
                        )
                      }
                      onInputChange={(_e, newInputValue, reason) => {
                        if (reason === 'input') {
                          const matchingType = TIMES?.find(
                            (item: any) => item?.title === newInputValue,
                          );
                          if (matchingType) {
                            setValue(
                              `${selectedFieldPath}.operatingHours.startTime`,
                              matchingType.value,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          } else {
                            setValue(
                              `${selectedFieldPath}.operatingHours.startTime`,
                              newInputValue,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          }
                        }
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.operatingHours.endTime`}
                render={({field, fieldState: {error}}) => (
                  <div className="w-[90px]">
                    <AppMUIAutocomplete
                      freeSolo
                      disableClearable
                      options={TIMES.map(i => i.title)}
                      label="עד"
                      size="small"
                      required
                      value={field.value}
                      onChange={(_, v) =>
                        setValue(
                          `${selectedFieldPath}.operatingHours.endTime`,
                          v,
                          {shouldValidate: true, shouldDirty: true},
                        )
                      }
                      onInputChange={(_e, newInputValue, reason) => {
                        if (reason === 'input') {
                          const matchingType = TIMES?.find(
                            (item: any) => item?.title === newInputValue,
                          );
                          if (matchingType) {
                            setValue(
                              `${selectedFieldPath}.operatingHours.endTime`,
                              matchingType.value,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          } else {
                            setValue(
                              `${selectedFieldPath}.operatingHours.endTime`,
                              newInputValue,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          }
                        }
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />
            </div>
          </div>

          <Controller
            name={`${selectedFieldPath}.checklistNotesReset.type`}
            control={control}
            render={({field}) => {
              return (
                <div className="flex items-center gap-2.5">
                  <p className="m-0 text-xl font-bold text-darkBlue">
                    איפוס הערות בצ’קליסטים
                  </p>

                  <div className="flex items-center gap-2">
                    <Radio
                      color="secondary"
                      size="small"
                      sx={{p: 1}}
                      value={CHECKLIST_NOTES_RESET_TYPES.START_OF_DAY}
                      checked={
                        field.value === CHECKLIST_NOTES_RESET_TYPES.START_OF_DAY
                      }
                      onChange={e => field.onChange(e.target.value)}
                    />
                    <p className="m-0 text-base text-textColor">כל תחילת יום</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Radio
                      color="secondary"
                      size="small"
                      sx={{p: 1}}
                      value={CHECKLIST_NOTES_RESET_TYPES.END_OF_DAY}
                      checked={
                        field.value === CHECKLIST_NOTES_RESET_TYPES.END_OF_DAY
                      }
                      onChange={e => field.onChange(e.target.value)}
                    />
                    <p className="m-0 text-base text-textColor">כל סוף יום</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Radio
                      color="secondary"
                      size="small"
                      sx={{p: 1}}
                      value={CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME}
                      checked={
                        field.value ===
                        CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME
                      }
                      onChange={e => field.onChange(e.target.value)}
                    />
                    <p className="m-0 text-base text-textColor">בשעה ספציפית</p>

                    <Controller
                      control={control}
                      name={`${selectedFieldPath}.checklistNotesReset.time`}
                      render={({field: timeField, fieldState: {error}}) => (
                        <div className="w-[90px]">
                          <AppMUIAutocomplete
                            freeSolo
                            disableClearable
                            options={TIMES.map(i => i.title)}
                            size="small"
                            disabled={
                              field.value !==
                              CHECKLIST_NOTES_RESET_TYPES.SPECIFIC_TIME
                            }
                            value={timeField.value}
                            onChange={(_, v) =>
                              setValue(
                                `${selectedFieldPath}.checklistNotesReset.time`,
                                v,
                                {shouldValidate: true, shouldDirty: true},
                              )
                            }
                            onInputChange={(_e, newInputValue, reason) => {
                              if (reason === 'input') {
                                const matchingType = TIMES?.find(
                                  (item: any) => item?.title === newInputValue,
                                );
                                if (matchingType) {
                                  setValue(
                                    `${selectedFieldPath}.checklistNotesReset.time`,
                                    matchingType.value,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    },
                                  );
                                } else {
                                  setValue(
                                    `${selectedFieldPath}.checklistNotesReset.time`,
                                    newInputValue,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    },
                                  );
                                }
                              }
                            }}
                            error={!!error}
                            helperText={error?.message}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex gap-3 p-2.5 border border-gray70 rounded-lg shadow-sm">
            <span className="text-xl text-gray font-light self-center w-[20px]">
              {String(index + 1).padStart(2, '0')}
            </span>

            <div className="flex gap-2">
              <img
                src={SVG.clockIcon}
                alt="icon"
                className="w-5 h-5 object-contain self-center"
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.shifts.${index}.from`}
                render={({field, fieldState: {error}}) => (
                  <div className="w-[90px]">
                    <AppMUIAutocomplete
                      freeSolo
                      disableClearable
                      options={TIMES.map(i => i.title)}
                      label="מ-"
                      size="small"
                      required
                      value={field.value}
                      onChange={(_, v) =>
                        setValue(
                          `${selectedFieldPath}.shifts.${index}.from`,
                          v,
                          {shouldValidate: true, shouldDirty: true},
                        )
                      }
                      onInputChange={(_e, newInputValue, reason) => {
                        if (reason === 'input') {
                          const matchingType = TIMES?.find(
                            (item: any) => item?.title === newInputValue,
                          );
                          if (matchingType) {
                            setValue(
                              `${selectedFieldPath}.shifts.${index}.from`,
                              matchingType.value,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          } else {
                            setValue(
                              `${selectedFieldPath}.shifts.${index}.from`,
                              newInputValue,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          }
                        }
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.shifts.${index}.to`}
                render={({field, fieldState: {error}}) => (
                  <div className="w-[90px]">
                    <AppMUIAutocomplete
                      freeSolo
                      disableClearable
                      options={TIMES.map(i => i.title)}
                      label="עד"
                      size="small"
                      required
                      value={field.value}
                      onChange={(_, v) =>
                        setValue(`${selectedFieldPath}.shifts.${index}.to`, v, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      onInputChange={(_e, newInputValue, reason) => {
                        if (reason === 'input') {
                          const matchingType = TIMES?.find(
                            (item: any) => item?.title === newInputValue,
                          );
                          if (matchingType) {
                            setValue(
                              `${selectedFieldPath}.shifts.${index}.to`,
                              matchingType.value,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          } else {
                            setValue(
                              `${selectedFieldPath}.shifts.${index}.to`,
                              newInputValue,
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              },
                            );
                          }
                        }
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />
            </div>

            <Controller
              control={control}
              name={`${selectedFieldPath}.shifts.${index}.name`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="שם משמרת"
                    placeholder="שם משמרת"
                    className="border-none"
                    required
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <button
              type="button"
              className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
              onClick={() => {
                const shiftId = getValues(
                  `${selectedFieldPath}.shifts.${index}.id` as const,
                ) as string;
                const shiftName = getValues(
                  `${selectedFieldPath}.shifts.${index}.name` as const,
                ) as string;

                const result = canDeleteShift(
                  network,
                  selectedFieldPath,
                  shiftId,
                  shiftName,
                );

                if (!result.allowed) {
                  showSnackbar({
                    message: result.message,
                    type: 'error',
                    duration: 5000,
                  });
                  return;
                }

                remove(index);
              }}>
              <img
                src={SVG.deleteGrayIcon}
                alt="del"
                className="w-4 h-[18px] object-contain"
              />
            </button>
          </div>
        ))}

        <div>
          <button
            type="button"
            className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
            onClick={handleAddShift}>
            <img
              src={SVG.addDarkBlueIcon}
              alt="add"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Shifts;
